import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { FormField } from '../../../interfaces/form.interface';
import { DropdownData, FilteredValue, InputType } from '../../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../../model/reactive-form-value.utils';

const DEFAULT_SELECT_PLACEHOLDER = 'Seleccionar';

@Component({
    selector: 'app-reactive-select-field',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './select-field.component.html',
    styleUrls: ['./select-field.component.scss'],
})
export class ReactiveSelectFieldComponent<T> {
    @Input() field!: FormField<T>;

    @Input() model!: FormGroup;

    @Input() isEditable = false;

    @Input() describedById = '';

    @Input() isInvalid = false;

    @Input() filterInputParcelas = false;

    public readonly inputType = InputType;

    public get filteredOptions(): FilteredValue<T>[] {
        return (this.field?.input?.data as DropdownData<T>).filtered ?? [];
    }

    public get virtualScrollEnabled(): boolean {
        return this.field.input?.dropdownConfiguration?.virtualScroll ?? this.filteredOptions.length > 100;
    }

    public get virtualScrollItemSize(): number {
        return this.field.input?.dropdownConfiguration?.virtualScrollItemSize ?? 40;
    }

    public get dataKey(): string | null {
        const firstRealOption = this.filteredOptions.find((option) => option.value !== null && option.value !== undefined);

        if (!firstRealOption) {
            return ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        }

        return typeof firstRealOption.value === 'object'
            ? ReactiveFormValueUtils.getFieldControlValueKey(this.field)
            : null;
    }

    public getSelectPlaceholder(): string {
        const selectedLabel = this.resolveSelectedControlLabel();

        return selectedLabel !== '' ? selectedLabel : DEFAULT_SELECT_PLACEHOLDER;
    }

    public updateModel(): void {
        if (!this.field.input?.selectButtonConfiguration?.onClick) {
            return;
        }

        const onClick = this.field.input.selectButtonConfiguration.onClick(this.field as T);

        onClick.then((data) => {
            Object.entries(data).forEach(([key, value]) => {
                this.model.patchValue({ [key]: value });
            });

            return;
        }).catch((error: string) => {
            console.log(error);
        });
    }

    private resolveSelectedControlLabel(): string {
        const fieldName = this.field?.name ?? '';
        const controlValue = fieldName !== '' ? this.model?.get(fieldName)?.value : null;

        if (!ReactiveFormValueUtils.hasNonEmptyValue(controlValue)) {
            return '';
        }

        const matchedOptionLabel = this.resolveMatchedOptionLabel(controlValue);

        if (matchedOptionLabel !== '') {
            return matchedOptionLabel;
        }

        return this.normalizeDisplayLabel(ReactiveFormValueUtils.formatPreviewValue(controlValue, this.field));
    }

    private resolveMatchedOptionLabel(controlValue: unknown): string {
        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(this.field);
        const matchedOption = this.filteredOptions.find((option) =>
            ReactiveFormValueUtils.areValuesEquivalent(option.value, controlValue, primaryKey, identityKeys)
        );

        return this.normalizeDisplayLabel(matchedOption?.label ?? '');
    }

    private normalizeDisplayLabel(value: unknown): string {
        const label = String(value ?? '').trim();

        return ReactiveFormValueUtils.isBadDisplayToken(label) ? '' : label;
    }
}
